import { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Stack,
    Grid,
    CircularProgress,
    Box,
    Tooltip,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Button from 'domains/core/components/Button';
import { Campaign, ABTestType } from 'domains/campaigns/types';
import { DeviceThermostat } from '@mui/icons-material';
import useGetABTestInsights from 'hooks/mutations/useGetABTestInsights';
import useDeclareABTestWinner from 'hooks/mutations/useDeclareABTestWinner';
import QueryKeys from 'hooks/queries/keys';
import ABTestInsightCard from './ABTestInsightCard';
import { themeColors } from 'colors';
import { URLPaths } from 'models/enums';

type ABTestInsightsModalProps = {
    campaign: Campaign;
    isOpen: boolean;
    handleModalClose: () => void;
};

type InsightData = {
    label: string;
    title: string;
    subtitle: string;
    deliveryRate: string;
    openRate: string;
    clickRate: string;
    sendId: number;
};

const ABTestInsightsModal = ({ campaign, isOpen, handleModalClose }: ABTestInsightsModalProps) => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const [selectedVariant, setSelectedVariant] = useState<number | null>(null);

    // Check if testResults exist in abTestConfig
    const hasTestResults = !!(campaign.abTestConfig as any)?.testResults;

    // Extract winnerSendId from abTestConfig when testResults exist
    const winnerSendId = hasTestResults ? (campaign.abTestConfig as any)?.winnerSendId : null;

    // API mutation hook for fetching A/B test insights
    const { mutate: getABTestInsights, data: insightsData, isLoading, isError } = useGetABTestInsights({
        onSuccess: (data) => {
            // Successfully fetched A/B test insights
        },
        onError: (error) => {
            // Error fetching A/B test insights
        },
    });

    // API mutation hook for declaring A/B test winner
    const { mutate: declareWinner, isLoading: isDeclaring } = useDeclareABTestWinner({
        onSuccess: (data) => {
            // Successfully declared winner - invalidate campaign query to refetch updated data
            queryClient.invalidateQueries(QueryKeys.CURATION_ID(campaign.id));
            handleModalClose();
        },
        onError: (error) => {
            // Error declaring winner
        },
    });

    // Fetch insights when modal opens (only if testResults is not available)
    useEffect(() => {
        if (isOpen && campaign.id && !hasTestResults) {
            getABTestInsights({ campaignId: campaign.id });
        }
    }, [isOpen, campaign.id, hasTestResults, getABTestInsights]);

    // Helper function to safely format percentage values
    const formatPercentage = (value: string | number | null | undefined): string => {
        if (value === null || value === undefined || value === '') {
            return 'NaN';
        }
        return `${value}%`;
    };

    // Helper function to determine what's being tested
    // Priority: Use stored comparisonFields if available, otherwise use fallback based on testType
    const testingLabel = useMemo(() => {
        const testType = campaign.abTestConfig?.testOn;

        // Determine the prefix based on test type
        const prefix = testType === ABTestType.SEND_TIME ? 'Testing' : 'Testing Content';

        // If comparisonFields exist in abTestConfig, use them
        if (campaign.abTestConfig?.comparisonFields && campaign.abTestConfig.comparisonFields.length > 0) {
            return `${prefix}: ${campaign.abTestConfig.comparisonFields.join(', ')}`;
        }

        // Fallback logic based on testType
        if (testType === ABTestType.CONTENT) {
            return `${prefix}: Subject line, Preview text, Body or Sender profile`;
        }
        if (testType === ABTestType.SEND_TIME) {
            return `${prefix}: Best time of day to send`;
        }

        // Default fallback
        return 'Testing Content: Subject line, Preview text, Body or Sender profile';
    }, [campaign.abTestConfig]);

    // Process data - either from testResults or API response
    const processedInsightData = useMemo((): InsightData[] => {
        const labels = ['A', 'B'];

        // If testResults exist in abTestConfig, use that data
        if (hasTestResults) {
            const testResults = (campaign.abTestConfig as any).testResults;
            const insights = Object.values(testResults) as any[];

            return insights.map((insight, index) => {
                // Find the corresponding send in campaign.sends by sendId
                const correspondingSend = campaign.sends.find((send) => send.id === insight.sendId);

                return {
                    label: labels[index] || `${index + 1}`,
                    title: correspondingSend?.name || `Email ${labels[index]}`,
                    subtitle: correspondingSend?.subjectLine || '',
                    deliveryRate: formatPercentage(insight.deliveredRate),
                    openRate: formatPercentage(insight.openRate),
                    clickRate: formatPercentage(insight.clickRate),
                    sendId: insight.sendId,
                };
            });
        }

        // Otherwise, use API data
        if (!insightsData?.abTestInsights) return [];

        const insights = Object.values(insightsData.abTestInsights);

        return insights.map((insight, index) => {
            // Find the corresponding send in campaign.sends by send_id
            const correspondingSend = campaign.sends.find((send) => send.id === insight.send_id);

            return {
                label: labels[index] || `${index + 1}`,
                title: correspondingSend?.name || `Email ${labels[index]}`,
                subtitle: correspondingSend?.subjectLine || '',
                deliveryRate: formatPercentage(insight.delivered_rate),
                openRate: formatPercentage(insight.open_rate),
                clickRate: formatPercentage(insight.click_rate),
                sendId: insight.send_id,
            };
        });
    }, [hasTestResults, campaign, insightsData]);

    // Check if insights data is incomplete (still being calculated)
    // For A/B tests, we expect 2 sends. Show calculating state if we have less than 2 insights.
    const isInsightsIncomplete = !hasTestResults && processedInsightData.length < 2 && !isLoading && !isError;

    const handleVariantSelect = (sendId: number) => {
        setSelectedVariant(sendId);
    };

    const handleDone = () => {
        // If test is already completed, just close the modal
        if (hasTestResults) {
            handleModalClose();
            return;
        }

        // If no variant is selected, just close the modal
        if (!selectedVariant) {
            handleModalClose();
            return;
        }

        // Prepare the request body for declaring winner
        const requestBody: any = {
            campaignId: campaign.id,
            winnerSendId: selectedVariant,
            abTestInsights: {},
        };

        // Build abTestInsights from the API data
        if (insightsData?.abTestInsights) {
            requestBody.abTestInsights = insightsData.abTestInsights;
        }

        // Call the API to declare winner
        declareWinner(requestBody);
    };

    const handleGoToDashboard = () => {
        history.push(URLPaths.INSIGHTS);
        handleModalClose();
    };

    return (
        <Dialog maxWidth="lg" fullWidth open={isOpen} onClose={handleModalClose}>
            <DialogTitle>
                <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="end" justifyContent="space-between">
                        <Stack direction="row" alignItems="end" gap={1}>
                            <DeviceThermostat />
                            <Typography variant="h5" sx={{ fontWeight: 500 }}>
                                a/b test insights
                            </Typography>
                        </Stack>
                        {/* Status Label */}
                        <Tooltip
                            title={
                                hasTestResults
                                    ? "You're seeing results from when the winner was declared."
                                    : isInsightsIncomplete
                                    ? 'Test insights are being calculated and will be available shortly.'
                                    : "You're seeing live insights as data comes in."
                            }
                            arrow
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 0.75,
                                    px: 2,
                                    py: 0.75,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: hasTestResults
                                            ? themeColors.success.main
                                            : themeColors.warning.main,
                                        mx: 1,
                                    }}
                                />

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    {hasTestResults ? 'Test ended' : 'Test in progress'}
                                </Typography>
                            </Box>
                        </Tooltip>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {testingLabel}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {/* Show loading/error only when fetching from API (not when using testResults) */}
                {!hasTestResults && isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <CircularProgress />
                    </Box>
                ) : !hasTestResults && isError ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <Typography variant="body1" color="error">
                            Error loading insights. Please try again.
                        </Typography>
                    </Box>
                ) : isInsightsIncomplete ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px',
                            textAlign: 'center',
                            px: 4,
                        }}
                    >
                        <CircularProgress size={48} sx={{ mb: 3 }} />
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                            Calculating Test Insights
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '500px' }}>
                            This usually takes a few moments. Please check back shortly to view the results.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        <Typography variant="body1">
                            Choose a winner from insights to be send to remaining audience
                        </Typography>

                        <Stack direction="row" spacing={2}>
                            {/* Metric Labels Column */}
                            <Grid item xs={12} md={3}>
                                <Stack spacing={2}>
                                    {/* Empty space for header alignment */}
                                    <div style={{ height: '68px' }} />

                                    {/* Metric labels */}
                                    <Stack spacing={1} sx={{ pt: 2 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{ height: '40px', display: 'flex', alignItems: 'center' }}
                                        >
                                            Delivery Rate
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ height: '40px', display: 'flex', alignItems: 'center' }}
                                        >
                                            Open Rate
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ height: '40px', display: 'flex', alignItems: 'center' }}
                                        >
                                            Click Through Rate
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* Data Cards */}
                            <Stack direction="row" sx={{ width: '100%' }} spacing={4}>
                                {processedInsightData.map((data: InsightData) => (
                                    <ABTestInsightCard
                                        key={data.sendId}
                                        label={data.label}
                                        title={data.title}
                                        subtitle={data.subtitle}
                                        deliveryRate={data.deliveryRate}
                                        openRate={data.openRate}
                                        clickRate={data.clickRate}
                                        isSelected={selectedVariant === data.sendId}
                                        winner={winnerSendId === data.sendId}
                                        isTestCompleted={hasTestResults}
                                        onSelect={() => handleVariantSelect(data.sendId)}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: 3 }}>
                <Button variant="text" onClick={handleGoToDashboard}>
                    Go to dashboard
                </Button>
                <Button variant="contained" onClick={handleDone} disabled={isDeclaring}>
                    {isDeclaring ? 'Declaring Winner...' : 'Done'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ABTestInsightsModal;
